import { VStack } from "@chakra-ui/layout"
import React, { useEffect, useMemo } from "react"
import { Control, Controller } from "react-hook-form"

import { GetAuthRegister } from "~/api/auth/register/get"
import { useSWRForGetSkills } from "~/api/skills/get"
import Selectbox from "~/components/atoms/Selectbox"
import { DoubleColumnsGrid } from "~/components/layouts/DoubleColumnsGrid"
import { FormContainer } from "~/components/molecules/FormContainer"
import { InputAutoSuggestMultiple } from "~/components/molecules/InputAutoSuggestMultiple"
import SelectboxMultiple from "~/components/molecules/SelectboxMultiple"
import { SpacingSize } from "~/consts/style-mixins"
import { createOptions } from "~/services/createOptions"

import { Section } from "../components/Section"
import { LABELS, VALIDATE_MAP } from "../constants"
import { HackedPostAuthRegisterBody } from "../types"

type Props = {
  control: Control<HackedPostAuthRegisterBody>
  formMasterData: GetAuthRegister
}

export const Experience: React.VFC<Props> = ({ control, formMasterData }) => {
  const {
    register,
    unregister,
    _formState: { errors },
    // NOTE: useForm().watchの内部関数
    // @see https://github.com/react-hook-form/react-hook-form/blob/master/src/logic/createFormControl.ts#L818-L844
    _getWatch: watch,
  } = control

  const { data: skillsResponse } = useSWRForGetSkills()

  const options = useMemo(
    () => ({
      studentExperienceTypes: createOptions(formMasterData.student_experience_types),
      studentAchievementTypes: createOptions(formMasterData.student_achievement_types),
      experiencePeriods: createOptions(formMasterData.experience_periods, { isAddInitialOption: true }),
    }),
    [formMasterData]
  )

  const watchProgrammingExperience = watch("programming_experience", {}, true) as string | undefined
  const watchDesignExperience = watch("design_experience", {}, true) as string | undefined

  /** プログラミング経験とデザイン経験のどちらかが入力済みかつ"経験なし"ではない時true */
  const isShownSkillsEdit = useMemo((): boolean => {
    if (watchProgrammingExperience && watchProgrammingExperience !== "経験なし") {
      return true
    }
    if (watchDesignExperience && watchDesignExperience !== "経験なし") {
      return true
    }
    return false
  }, [watchProgrammingExperience, watchDesignExperience])

  // スキルを表示しなくなった時、skillsの内容を消す
  useEffect(() => {
    if (!isShownSkillsEdit) {
      unregister("skills")
    }
  }, [isShownSkillsEdit])

  return (
    <Section title="経験・実績">
      <VStack spacing={SpacingSize._2XL}>
        <FormContainer
          label={LABELS.studentExperienceTypeIds}
          errorMessage={errors.student_experience_type_ids?.message}
        >
          <Controller
            name="student_experience_type_ids"
            control={control}
            shouldUnregister
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <SelectboxMultiple
                label={LABELS.studentExperienceTypeIds}
                options={options.studentExperienceTypes ?? []}
                selected={value ?? []}
                onChange={onChange}
                error={!!error}
                exclusiveOptionValue={options.studentExperienceTypes[0].value}
              />
            )}
          />
        </FormContainer>

        <DoubleColumnsGrid>
          <FormContainer label={LABELS.programmingExperience} errorMessage={errors.programming_experience?.message}>
            <Selectbox
              options={options.experiencePeriods ?? []}
              selectProps={register("programming_experience", {
                shouldUnregister: true,
              })}
              isError={!!errors.programming_experience}
            />
          </FormContainer>

          <FormContainer label={LABELS.designExperience} errorMessage={errors.design_experience?.message}>
            <Selectbox
              options={options.experiencePeriods ?? []}
              selectProps={register("design_experience", {
                shouldUnregister: true,
              })}
              isError={!!errors.design_experience}
            />
          </FormContainer>
        </DoubleColumnsGrid>

        {isShownSkillsEdit && (
          <FormContainer label={LABELS.skills}>
            <Controller
              name="skills"
              control={control}
              shouldUnregister
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <InputAutoSuggestMultiple
                  placeholder="スキル・ツール名"
                  getter={(text: string) => text}
                  suggestions={skillsResponse?.skills ?? []}
                  defaultValue={value}
                  onChange={onChange}
                  errorMessage={error?.message}
                />
              )}
            />
          </FormContainer>
        )}
      </VStack>
    </Section>
  )
}
